import { useEffect, useRef, useState } from "react";

import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { queryClient } from "@impero-com/common/utils/query_client";
import {
    AILoadingIndicator,
    AiMagicSparkles,
    AiErrorLLMRejected,
    AIRetry,
    AIViewSummarySection,
    Button,
    CollapseExpandTextButton,
    CollapseTransition,
    InfoTooltip,
    renderMarkdown,
    Tooltip,
} from "@impero-com/ui";
import { rawPalette } from "@impero-com/ui/styles/theme.css";
import { AxiosError } from "axios";
import { cx } from "class-variance-authority";

import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import {
    getCompareRiskMapQueryKey,
    prefetchCompareRiskMap,
    RiskMapKind,
    useCompareRiskMapQuery,
} from "../queries/useCompareRiskMapQuery";

import * as style from "./RiskMapAISummary.css";

const PREFETCH_HOVER_DELAY_MS = 120;

interface RiskMapAISummaryProps {
    riskDirectoryId: number;
    kind: RiskMapKind;
    from: string;
    to: string;
    enabled: boolean;
    onEnable: () => void;
    dataUnavailable?: boolean;
}

export function RiskMapAISummary({
    riskDirectoryId,
    kind,
    from,
    to,
    enabled,
    onEnable,
    dataUnavailable,
}: RiskMapAISummaryProps) {
    const aiRiskMapInsightsEnabled = useFeatureFlag("aiRiskMapInsights");
    const { t } = useTranslation();
    const prefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (prefetchTimerRef.current !== null) {
                clearTimeout(prefetchTimerRef.current);
            }
        };
    }, []);

    if (!aiRiskMapInsightsEnabled) {
        return null;
    }

    // To minimize API calls, we re-use locally cached data
    const hasCachedData =
        queryClient.getQueryData(
            getCompareRiskMapQueryKey({ riskDirectoryId, kind, from, to })
        ) !== undefined;
    const showContent = enabled || hasCachedData;

    const isSameDate = from === to;
    const generationDisabled = isSameDate || dataUnavailable || showContent;

    const disabledTooltip = isSameDate
        ? t("RiskDirectory_InsightsAISummary_DisabledTooltip")
        : undefined;

    const handlePrefetchHover = () => {
        if (generationDisabled || hasCachedData) {
            return;
        }
        if (prefetchTimerRef.current !== null) {
            return;
        }
        prefetchTimerRef.current = setTimeout(() => {
            prefetchTimerRef.current = null;
            void prefetchCompareRiskMap({ riskDirectoryId, kind, from, to });
        }, PREFETCH_HOVER_DELAY_MS);
    };

    const handlePrefetchCancelTimer = () => {
        if (prefetchTimerRef.current !== null) {
            clearTimeout(prefetchTimerRef.current);
            prefetchTimerRef.current = null;
        }
    };

    return (
        <div className={style.root}>
            <RiskMapAISummaryHeader
                onButtonClick={onEnable}
                buttonDisabled={generationDisabled}
                disabledTooltip={disabledTooltip}
                onPrefetchHover={handlePrefetchHover}
                onPrefetchCancelTimer={handlePrefetchCancelTimer}
            />

            <CollapseTransition collapsed={!showContent}>
                <RiskMapAISummaryContent
                    riskDirectoryId={riskDirectoryId}
                    kind={kind}
                    from={from}
                    to={to}
                />
            </CollapseTransition>
        </div>
    );
}

interface RiskMapAISummaryContentProps {
    riskDirectoryId: number;
    kind: RiskMapKind;
    from: string;
    to: string;
}

function RiskMapAISummaryContent({
    riskDirectoryId,
    kind,
    from,
    to,
}: RiskMapAISummaryContentProps) {
    const { t } = useTranslation();
    const [collapsed, setCollapsed] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);
    const { data, status, error } = useCompareRiskMapQuery(
        { riskDirectoryId, kind, from, to },
        true
    );

    const handleRetry = () => {
        queryClient.refetchQueries({
            queryKey: getCompareRiskMapQueryKey({
                riskDirectoryId,
                kind,
                from,
                to,
            }),
        });
    };

    if (status === "pending") {
        return (
            <div className={style.noContentContainer}>
                <AILoadingIndicator
                    layout="vertical"
                    label={t("RiskDirectory_InsightsAISummary_Loading")}
                />
            </div>
        );
    }

    if (status === "error") {
        const riskMapTooLarge =
            error instanceof AxiosError && error.status === 413;
        return (
            <div className={style.noContentContainer}>
                {riskMapTooLarge ? (
                    <AiErrorLLMRejected
                        message={t("RiskDirectory_InsightsAISummary_TooLarge")}
                    />
                ) : (
                    <AIRetry
                        message={t("RiskDirectory_InsightsAISummary_Error")}
                        onRetry={handleRetry}
                        retryLabel={t("RiskDirectory_InsightsAISummary_Retry")}
                    />
                )}
            </div>
        );
    }

    return (
        <div
            className={cx(style.contentContainer, collapsed && style.collapsed)}
        >
            <CollapseTransition collapsed={collapsed}>
                <div className={style.contentSectionsContainer} ref={textRef}>
                    <div className={style.contentSectionItem}>
                        <AIViewSummarySection
                            title={t(
                                "RiskDirectory_InsightsAISummary_SectionOverview"
                            )}
                            iconVariant="lightbulb"
                            colorVariant={rawPalette.base.red[500]}
                        >
                            {renderMarkdown(data.summary)}
                        </AIViewSummarySection>
                    </div>
                    <div className={style.contentSectionItem}>
                        <AIViewSummarySection
                            title={t(
                                "RiskDirectory_InsightsAISummary_SectionProgression"
                            )}
                            iconVariant="rise"
                            colorVariant={rawPalette.base.lightBlue[400]}
                        >
                            {renderMarkdown(data.progression)}
                        </AIViewSummarySection>
                    </div>
                    <div className={style.contentSectionItem}>
                        <AIViewSummarySection
                            title={t(
                                "RiskDirectory_InsightsAISummary_SectionDistribution"
                            )}
                            iconVariant="target"
                            colorVariant={rawPalette.primary}
                        >
                            {renderMarkdown(data.distribution)}
                        </AIViewSummarySection>
                    </div>
                </div>
            </CollapseTransition>
            <CollapseExpandTextButton
                onClick={() => setCollapsed(!collapsed)}
                collapsed={collapsed}
                expandLabel={t("RiskDirectory_InsightsAISummary_Expand")}
                collapseLabel={t("RiskDirectory_InsightsAISummary_Collapse")}
            />
        </div>
    );
}

interface RiskMapAISummaryHeaderProps {
    onButtonClick: () => void;
    buttonDisabled: boolean;
    disabledTooltip: string | undefined;
    onPrefetchHover: () => void;
    onPrefetchCancelTimer: () => void;
}
function RiskMapAISummaryHeader({
    onButtonClick,
    buttonDisabled,
    disabledTooltip,
    onPrefetchHover,
    onPrefetchCancelTimer,
}: RiskMapAISummaryHeaderProps) {
    const { t } = useTranslation();

    const button = (
        <Button
            disabled={buttonDisabled}
            variant="default"
            onClick={onButtonClick}
            icon={<EditOutlined />}
            onMouseEnter={onPrefetchHover}
            onMouseLeave={onPrefetchCancelTimer}
            onFocus={onPrefetchHover}
            onBlur={onPrefetchCancelTimer}
        >
            {t("RiskDirectory_InsightsAISummary_GenerateButton")}
        </Button>
    );

    return (
        <div className={style.headerRoot}>
            <div className={style.headerTextContainer}>
                <div className={style.headerTitleContainer}>
                    <AiMagicSparkles size={18} />
                    <span className={style.headerTitle}>
                        {t("RiskDirectory_InsightsAISummary_Title")}
                    </span>
                    <sup className={style.betaLabel}>
                        {t("RiskDirectory_InsightsAISummary_BetaLabel")}
                    </sup>
                    <InfoTooltip
                        text={t("RiskDirectory_InsightsAISummary_Tooltip")}
                        size="small"
                        className={style.infoTooltip}
                    />
                </div>
                <span className={style.headerDescription}>
                    {t("RiskDirectory_InsightsAISummary_Description")}
                </span>
            </div>
            {disabledTooltip && buttonDisabled ? (
                <Tooltip title={disabledTooltip}>{button}</Tooltip>
            ) : (
                button
            )}
        </div>
    );
}
